/**
 * AI token usage: presentation rules shared by the header meter, the Clients
 * module and the JLL usage overview.
 *
 * Severity is derived in exactly one place so a bar, a chip and a table row can
 * never disagree about whether a client is "at risk".
 */

/** Usage is over the allowance — billed as overage. */
export const USAGE_SEVERITY_CRITICAL = 'critical';
/** Usage has passed the client's alert threshold. */
export const USAGE_SEVERITY_WARNING = 'warning';
/** Comfortably inside the allowance. */
export const USAGE_SEVERITY_OK = 'ok';

/** Fallback alert threshold when a client has none stored. Mirrors the DB default. */
export const DEFAULT_ALERT_THRESHOLD_PERCENT = 80;

/**
 * Green / amber / red, matching the MUI palette the rest of the app uses.
 * `main` is for bars and icons, `soft` for chip and row backgrounds.
 */
export const USAGE_SEVERITY_COLORS = {
  [USAGE_SEVERITY_OK]: { main: '#2e7d32', soft: 'rgba(46, 125, 50, 0.12)', label: 'On track' },
  [USAGE_SEVERITY_WARNING]: { main: '#ed6c02', soft: 'rgba(237, 108, 2, 0.12)', label: 'Approaching limit' },
  [USAGE_SEVERITY_CRITICAL]: { main: '#d32f2f', soft: 'rgba(211, 47, 47, 0.12)', label: 'Limit exceeded' },
};

/** MUI `color` prop equivalents, for Chip/LinearProgress/Alert. */
export const USAGE_SEVERITY_MUI_COLOR = {
  [USAGE_SEVERITY_OK]: 'success',
  [USAGE_SEVERITY_WARNING]: 'warning',
  [USAGE_SEVERITY_CRITICAL]: 'error',
};

/**
 * Severity for a single usage percentage.
 * @param {number} percent uncapped percentage of the allowance consumed
 * @param {number} [alertThresholdPercent] amber threshold for this client
 */
export const getUsageSeverity = (percent, alertThresholdPercent = DEFAULT_ALERT_THRESHOLD_PERCENT) => {
  const value = Number(percent) || 0;
  const threshold = Number(alertThresholdPercent) || DEFAULT_ALERT_THRESHOLD_PERCENT;

  if (value >= 100) return USAGE_SEVERITY_CRITICAL;
  if (value >= threshold) return USAGE_SEVERITY_WARNING;
  return USAGE_SEVERITY_OK;
};

/**
 * Worst severity across the input and output meters — what the header badge and
 * the overview row should show.
 */
export const getSummarySeverity = (summary) => {
  if (!summary?.isConfigured) return USAGE_SEVERITY_OK;
  const threshold = summary.alertThresholdPercent ?? DEFAULT_ALERT_THRESHOLD_PERCENT;
  const input = getUsageSeverity(summary.inputUsagePercent, threshold);
  const output = getUsageSeverity(summary.outputUsagePercent, threshold);

  if (input === USAGE_SEVERITY_CRITICAL || output === USAGE_SEVERITY_CRITICAL) {
    return USAGE_SEVERITY_CRITICAL;
  }
  if (input === USAGE_SEVERITY_WARNING || output === USAGE_SEVERITY_WARNING) {
    return USAGE_SEVERITY_WARNING;
  }
  return USAGE_SEVERITY_OK;
};

/** Progress-bar width: the real percentage, clamped so the bar cannot overflow. */
export const clampPercent = (percent) => {
  const value = Number(percent) || 0;
  if (value < 0) return 0;
  return value > 100 ? 100 : value;
};

/**
 * Compact token count: 1_250_000 -> "1.25M". Full precision stays available in
 * tooltips via `formatTokens`.
 */
export const formatTokensCompact = (tokens) => {
  const value = Number(tokens) || 0;
  if (value < 1000) return String(value);
  if (value < 1_000_000) {
    const thousands = value / 1000;
    return `${thousands >= 100 ? Math.round(thousands) : Number(thousands.toFixed(1))}K`;
  }
  if (value < 1_000_000_000) {
    const millions = value / 1_000_000;
    return `${millions >= 100 ? Math.round(millions) : Number(millions.toFixed(2))}M`;
  }
  const billions = value / 1_000_000_000;
  return `${billions >= 100 ? Math.round(billions) : Number(billions.toFixed(2))}B`;
};

/** Grouped token count: 1250000 -> "1,250,000". */
export const formatTokens = (tokens) => (Number(tokens) || 0).toLocaleString();

/**
 * Money, with enough precision that sub-cent overage is still visible rather
 * than rounding to "$0.00".
 */
export const formatCost = (amount, currency = 'USD') => {
  const value = Number(amount) || 0;
  const fractionDigits = value > 0 && value < 0.01 ? 4 : 2;
  try {
    return value.toLocaleString(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  } catch {
    // Unknown currency code — show the amount with the code appended
    return `${value.toFixed(fractionDigits)} ${currency || ''}`.trim();
  }
};

/** Price per 1M tokens, e.g. "$3.00 / 1M". */
export const formatPricePerMillion = (price, currency = 'USD') =>
  price === null || price === undefined ? '—' : `${formatCost(price, currency)} / 1M`;

/** One decimal place, uncapped, so ">100%" overage stays legible. */
export const formatPercent = (percent) => `${(Number(percent) || 0).toFixed(1)}%`;

/**
 * Browser event the API layer fires after a successful AI run, so the header
 * meter refreshes without every widget component knowing about usage.
 */
export const AI_USAGE_CHANGED_EVENT = 'sda:ai-usage-changed';
